import React, { useEffect, useState, useCallback } from 'react';
import { ClipboardList, UserPlus, Check, ChevronsUpDown, GraduationCap, Trash2, Layers, ShieldAlert, History, Users, Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import PairedClassesTools from '../../components/school/PairedClassesTools';
import ImportEnrollmentTool from '../../components/school/ImportEnrollmentTool';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../../components/ui/command';
import { Badge } from '../../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { toast } from 'sonner';

export default function EnrollmentPage() {
  const { user } = useAuth();
  const { t } = useT();
  const orgId = useOrgId();
  const { selectedYear, selectedYearId } = useAcademicYear();
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [years, setYears] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [enrollmentsByStudent, setEnrollmentsByStudent] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterClass, setFilterClass] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ student_id: '', class_id: '', section_id: '', roll_no: '' });
  const [dialogSections, setDialogSections] = useState([]);
  const [saving, setSaving] = useState(false);
  // Searchable student picker (by name)
  const [studentPickerOpen, setStudentPickerOpen] = useState(false);
  const [studentQuery, setStudentQuery] = useState('');
  // Inline "reassign to a second class" popover state
  const [reassignFor, setReassignFor] = useState(null); // student id whose popover is open
  const [reassignForm, setReassignForm] = useState({ class_id: '', section_id: '', roll_no: '' });
  const [reassignSections, setReassignSections] = useState([]);
  const [reassigning, setReassigning] = useState(false);
  const [reassignExclude, setReassignExclude] = useState(new Set()); // class ids the student is already in
  // Remove-from-class confirmation state
  const [removeTarget, setRemoveTarget] = useState(null); // { student, enrollmentId, className }
  const [removing, setRemoving] = useState(false);
  // Change-section (same class) popover state
  const [sectionEditFor, setSectionEditFor] = useState(null); // student id whose popover is open
  const [sectionEditValue, setSectionEditValue] = useState('');
  const [sectionEditOptions, setSectionEditOptions] = useState([]);
  const [sectionEditing, setSectionEditing] = useState(false);

  // ----- Super-admin: bulk class unenroll + audit trail -----
  const isSuperAdmin = user?.role === 'super_admin';
  const [bulkTarget, setBulkTarget] = useState(null); // class object being unenrolled
  const [bulkPreview, setBulkPreview] = useState(null); // { class_name, academic_year_name, student_count, record_count }
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkConfirmText, setBulkConfirmText] = useState('');
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const openBulkRemove = async (cls) => {
    setBulkTarget(cls);
    setBulkConfirmText('');
    setBulkPreview(null);
    setBulkPreviewLoading(true);
    try {
      const p = await schoolApi.enrollmentRemovalPreview(orgId, cls.id, selectedYearId);
      setBulkPreview(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('enroll.previewFailed', 'Failed to load removal preview'));
      setBulkTarget(null);
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  const confirmBulkRemove = async () => {
    if (!bulkTarget) return;
    setBulkRemoving(true);
    try {
      const res = await schoolApi.removeClassEnrollments(bulkTarget.id, {
        organization_id: orgId,
        academic_year_id: selectedYearId,
        confirm_name: bulkConfirmText.trim(),
      });
      toast.success(res.message || t('enroll.bulkRemoved', 'Enrollments removed'));
      setBulkTarget(null);
      setBulkConfirmText('');
      setBulkPreview(null);
      load(); loadRefs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('enroll.bulkRemoveFailed', 'Failed to remove enrollments'));
    } finally {
      setBulkRemoving(false);
    }
  };

  const openAudit = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const rows = await schoolApi.listAuditLog(orgId, { limit: 100 });
      setAuditRows(rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('enroll.auditFailed', 'Failed to load audit trail'));
    } finally {
      setAuditLoading(false);
    }
  };

  const bulkNameMatches = !!bulkTarget && bulkConfirmText.trim().toLowerCase() === (bulkTarget.name || '').trim().toLowerCase();


  const loadRefs = useCallback(async () => {
    if (!orgId) return;
    const [cls, yrs, studs, enrolls] = await Promise.all([
      schoolApi.listClasses(orgId, selectedYearId), schoolApi.listYears(orgId),
      schoolApi.listStudents(orgId, { limit: 10000 }), schoolApi.listEnrollments(orgId, { academic_year_id: selectedYearId }),
    ]);
    setClasses(cls); setYears(yrs); setAllStudents(studs.students || []);
    // Map each student to all their active enrollments (used to surface dual-program assignments)
    const map = {};
    (enrolls || []).forEach((e) => {
      if (!e.student_id) return;
      (map[e.student_id] = map[e.student_id] || []).push({
        id: e.id, class_id: e.class_id, class_name: e.class_name, program_label: e.program_label,
      });
    });
    setEnrollmentsByStudent(map);
  }, [orgId, selectedYearId]);

  const load = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try { setLoading(true); const r = await schoolApi.listStudents(orgId, { class_id: filterClass, section_id: filterSection, academic_year_id: selectedYearId, limit: 10000 }); setStudents(r.students || []); }
    catch (e) { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [orgId, filterClass, filterSection, selectedYearId]);

  const loadFilterSections = useCallback(async () => {
    const d = filterClass ? await schoolApi.listSections(orgId, filterClass, selectedYearId) : [];
    setSections(d);
  }, [orgId, filterClass, selectedYearId]);

  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFilterSections(); }, [loadFilterSections]);

  const activeYear = selectedYear || years.find((y) => y.is_active) || years[0];

  const selectedStudent = allStudents.find((s) => s.id === form.student_id);
  const filteredStudents = studentQuery.trim()
    ? allStudents.filter((s) => (s.name || '').toLowerCase().includes(studentQuery.trim().toLowerCase()))
    : allStudents;

  // Program label is derived from a class's bulletin format. It is NOT a stored
  // enrollment attribute — only used as a read-only hint in the "reassign to a second
  // class" popover so admins can see which program a class belongs to.
  const PROGRAM_LABELS = { primary_fr: 'French', primary_pt: 'Portuguese', general_secondary: 'Secondary' };
  const classById = {};
  classes.forEach((c) => { classById[c.id] = c; });
  const programOf = (classId) => {
    const fmt = classById[classId]?.bulletin_format;
    return fmt ? (PROGRAM_LABELS[fmt] || 'General') : null;
  };

  const onDialogClass = async (class_id) => {
    setForm((f) => ({ ...f, class_id, section_id: '' }));
    setDialogSections(await schoolApi.listSections(orgId, class_id, selectedYearId));
  };

  // Paired-class conflict prompt: when the backend reports that the student is
  // already in the paired class under a DIFFERENT section, confirm before overwriting.
  const [pairConflict, setPairConflict] = useState(null); // { payload, conflicts, after }
  const [pairConfirming, setPairConfirming] = useState(false);

  const announcePair = (res) => {
    const created = res?.paired_enrolled || [];
    const synced = res?.paired_synced || [];
    if (created.length) toast.success(t('enroll.pairedEnrolled', 'Also enrolled in paired class: ') + created.join(', '));
    if (synced.length) toast.success(t('enroll.pairedSynced', 'Paired class section synced: ') + synced.join(', '));
  };

  // Calls enroll; if the backend returns pair_conflicts, defer the `after` callback
  // and open the confirmation dialog instead. Returns true if handled (no conflict).
  const enrollWithPairCheck = async (payload, after) => {
    const res = await schoolApi.createEnrollment(payload);
    if ((res?.pair_conflicts || []).length) {
      setPairConflict({ payload, conflicts: res.pair_conflicts, after });
      announcePair(res);
      return res;
    }
    announcePair(res);
    if (after) after();
    return res;
  };

  const confirmPairOverwrite = async () => {
    if (!pairConflict) return;
    setPairConfirming(true);
    try {
      const res = await schoolApi.createEnrollment({ ...pairConflict.payload, confirm_pair_overwrite: true });
      announcePair(res);
      const after = pairConflict.after;
      setPairConflict(null);
      if (after) after();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('enroll.pairSyncFailed', 'Failed to sync paired class'));
    } finally { setPairConfirming(false); }
  };

  const save = async () => {
    if (!form.student_id || !form.class_id) { toast.error(t('common.required')); return; }
    if (!form.section_id) { toast.error(t('enroll.sectionRequired', 'Please select a section')); return; }
    setSaving(true);
    try {
      await enrollWithPairCheck(
        { organization_id: orgId, student_id: form.student_id, class_id: form.class_id, section_id: form.section_id || null, academic_year_id: activeYear?.id, roll_no: form.roll_no || null },
        () => { setDialogOpen(false); setForm({ student_id: '', class_id: '', section_id: '', roll_no: '' }); load(); loadRefs(); });
      toast.success(t('common.saved'));
    } catch (e) { toast.error('Failed to assign'); } finally { setSaving(false); }
  };

  // One-time data fix: clear the legacy 'program' field that the old promotion tool
  // stamped onto the enrollment records it created. super_admin only, own org.
  const [clearingProg, setClearingProg] = useState(false);
  const runClearPromotionProgram = async () => {
    setClearingProg(true);
    try {
      const r = await schoolApi.clearPromotionProgram({ organization_id: orgId });
      toast.success(r?.message || t('enroll.programCleared', 'Legacy program field cleared'));
      load(); loadRefs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('enroll.clearProgramFailed', 'Failed to clear program field'));
    } finally { setClearingProg(false); }
  };

  // Open the inline reassign popover for a given student row
  const openReassign = async (student) => {
    setReassignFor(student.id);
    setReassignForm({ class_id: '', section_id: '', roll_no: student.roll_no || '' });
    setReassignSections([]);
    // Exclude classes the student is already enrolled in (so we always ADD a new class)
    const enrolled = new Set((enrollmentsByStudent[student.id] || []).map((e) => e.class_id).filter(Boolean));
    if (student.class_id) enrolled.add(student.class_id);
    setReassignExclude(enrolled);
  };

  const onReassignClass = async (class_id) => {
    setReassignForm((f) => ({ ...f, class_id, section_id: '' }));
    setReassignSections(await schoolApi.listSections(orgId, class_id, selectedYearId));
  };

  const saveReassign = async (studentId) => {
    if (!reassignForm.class_id) { toast.error(t('common.required')); return; }
    setReassigning(true);
    try {
      await enrollWithPairCheck(
        {
          organization_id: orgId,
          student_id: studentId,
          class_id: reassignForm.class_id,
          section_id: reassignForm.section_id || null,
          academic_year_id: activeYear?.id,
          roll_no: reassignForm.roll_no || null,
          allow_multi: true,
        },
        () => { setReassignFor(null); setReassignForm({ class_id: '', section_id: '', roll_no: '' }); setReassignSections([]); load(); loadRefs(); });
      toast.success(t('enroll.addedSecond', 'Student added to a second class'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('enroll.reassignFailed', 'Failed to add second class')); } finally { setReassigning(false); }
  };

  const reassignClassOptions = classes.filter((c) => !reassignExclude.has(c.id));

  // ----- Remove a class enrollment (must keep at least one class) -----
  const enrollmentsOf = (student) => enrollmentsByStudent[student.id] || [];

  const confirmRemove = async () => {
    if (!removeTarget?.enrollmentId) return;
    setRemoving(true);
    try {
      await schoolApi.deleteEnrollment(removeTarget.enrollmentId);
      toast.success(t('enroll.removedClass', 'Removed from class'));
      setRemoveTarget(null);
      load(); loadRefs();
    } catch (e) { toast.error(e?.response?.data?.detail || t('enroll.removeFailed', 'Failed to remove enrollment')); } finally { setRemoving(false); }
  };

  // ----- Change section within the SAME class -----
  const openSectionEdit = async (student) => {
    setSectionEditFor(student.id);
    setSectionEditValue(student.section_id || '');
    setSectionEditOptions(student.class_id ? await schoolApi.listSections(orgId, student.class_id, selectedYearId) : []);
  };

  const saveSectionEdit = async (student) => {
    setSectionEditing(true);
    try {
      await enrollWithPairCheck(
        {
          organization_id: orgId,
          student_id: student.id,
          class_id: student.class_id,
          section_id: sectionEditValue || null,
          academic_year_id: activeYear?.id,
          roll_no: student.roll_no || null,
          allow_multi: true, // dedupe by exact class -> updates THIS class's enrollment (section)
        },
        () => { setSectionEditFor(null); setSectionEditOptions([]); load(); loadRefs(); });
      toast.success(t('enroll.sectionChanged', 'Section updated'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('enroll.sectionChangeFailed', 'Failed to update section')); } finally { setSectionEditing(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader title={t('enroll.title')} subtitle={t('enroll.subtitle')} icon={ClipboardList}
        actions={<Button onClick={() => { setForm({ student_id: '', class_id: '', section_id: '', roll_no: '' }); setDialogSections([]); setStudentQuery(''); setStudentPickerOpen(false); setDialogOpen(true); }} className="gap-2" data-testid="assign-student-button"><UserPlus className="w-4 h-4" /> {t('enroll.assign')}</Button>} />

      {isSuperAdmin && (
        <Card className="p-4 mb-4 border-destructive/30" data-testid="bulk-unenroll-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-md bg-destructive/10 p-2 text-destructive"><ShieldAlert className="w-5 h-5" /></div>
              <div>
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  {t('enroll.bulkTitle', 'Bulk class unenroll')}
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wide">{t('enroll.superAdminOnly', 'Super admin')}</Badge>
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
                  {t('enroll.bulkSubtitle', 'Remove every enrollment for a class in the selected academic year only. Student profiles and grades are not affected.')}
                  {' '}<span className="font-medium text-foreground">{activeYear?.name || '-'}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <ImportEnrollmentTool
                orgId={orgId}
                academicYearId={selectedYearId}
                onImported={() => { load(); loadRefs(); }}
              />
              <PairedClassesTools
                orgId={orgId}
                academicYearId={selectedYearId}
                yearName={activeYear?.name}
                classes={classes}
                onSynced={() => { load(); loadRefs(); }}
              />
              <Button variant="outline" size="sm" className="gap-2" onClick={openAudit} data-testid="open-audit-trail-button">
                <History className="w-4 h-4" /> {t('enroll.auditTrail', 'Audit trail')}
              </Button>
              <Button variant="outline" size="sm" className="gap-2" onClick={runClearPromotionProgram} disabled={clearingProg} data-testid="clear-promotion-program-button">
                <History className="w-4 h-4" /> {clearingProg ? t('common.saving', 'Working...') : t('enroll.clearProgramField', 'Clear legacy program field')}
              </Button>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto">
            {classes.length === 0 ? (
              <p className="text-sm text-muted-foreground px-1 py-2">{t('common.noData', 'No data')}</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-3 py-2">{t('students.class', 'Class')}</th>
                  <th className="px-3 py-2">{t('enroll.enrolledStudents', 'Enrolled students')}</th>
                  <th className="px-3 py-2 text-right">{t('common.actions', 'Actions')}</th>
                </tr></thead>
                <tbody>
                  {classes.map((c) => (
                    <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/40" data-testid={`bulk-class-row-${c.id}`}>
                      <td className="px-3 py-2.5 font-medium">{c.name}</td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                          <Users className="w-3.5 h-3.5" />
                          <span className="tabular-nums font-medium text-foreground">{c.student_count ?? 0}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 text-destructive hover:text-destructive"
                          onClick={() => openBulkRemove(c)}
                          data-testid={`bulk-remove-enrollment-${c.id}`}
                        >
                          <Trash2 className="w-4 h-4" /> {t('enroll.removeEnrollment', 'Remove Enrollment')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      )}

      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={filterClass || 'all'} onValueChange={(v) => { setFilterClass(v === 'all' ? '' : v); setFilterSection(''); }}>
            <SelectTrigger className="w-full sm:w-56" data-testid="enroll-class-filter"><SelectValue placeholder={t('students.class')} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t('common.all')} {t('students.class')}</SelectItem>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterSection || 'all'} onValueChange={(v) => setFilterSection(v === 'all' ? '' : v)} disabled={!filterClass}>
            <SelectTrigger className="w-full sm:w-56" data-testid="enroll-section-filter"><SelectValue placeholder={t('students.section')} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t('common.all')} {t('students.section')}</SelectItem>{sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      <Card>
        {loading ? <div className="p-4"><LoadingState /></div> : students.length === 0 ? (
          <EmptyState icon={ClipboardList} title={t('common.noData')} description={t('enroll.subtitle')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-4 py-3">{t('students.rollNo')}</th>
                <th className="px-4 py-3">{t('common.name')}</th>
                <th className="px-4 py-3">{t('students.admissionNo')}</th>
                <th className="px-4 py-3">{t('students.class')}</th>
                <th className="px-4 py-3">{t('students.section')}</th>
                <th className="px-4 py-3 text-right">{t('common.actions', 'Actions')}</th>
              </tr></thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.id} className="border-b border-border last:border-0 hover:bg-muted/40" data-testid={`enroll-row-${s.id}`}>
                    <td className="px-4 py-3 tabular-nums">{s.roll_no || '-'}</td>
                    <td className="px-4 py-3 font-medium">{s.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{s.admission_no}</td>
                    <td className="px-4 py-3">{s.class_name || '-'}</td>
                    <td className="px-4 py-3"><Badge variant="outline">{s.section_name || '-'}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                      <Popover
                        open={reassignFor === s.id}
                        onOpenChange={(o) => { if (o) openReassign(s); else { setReassignFor(null); setReassignSections([]); } }}
                      >
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="gap-2" data-testid={`reassign-button-${s.id}`}>
                            <GraduationCap className="w-4 h-4" /> {t('enroll.reassignSecond', 'Reassign to a second class')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80" align="end" data-testid={`reassign-popover-${s.id}`}>
                          <div className="space-y-3">
                            <div>
                              <p className="text-sm font-medium">{t('enroll.reassignSecond', 'Reassign to a second class')}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{s.name}</p>
                            </div>
                            <div>
                              <Label>{t('students.class')} *</Label>
                              <Select value={reassignForm.class_id || ''} onValueChange={onReassignClass}>
                                <SelectTrigger data-testid={`reassign-class-${s.id}`}><SelectValue placeholder="-" /></SelectTrigger>
                                <SelectContent>
                                  {reassignClassOptions.length === 0
                                    ? <div className="px-3 py-2 text-sm text-muted-foreground">{t('enroll.noOtherClasses', 'No other classes available')}</div>
                                    : reassignClassOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              {reassignForm.class_id && programOf(reassignForm.class_id) && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {t('enroll.program', 'Program')}: <span className="font-medium text-foreground">{programOf(reassignForm.class_id)}</span>
                                </p>
                              )}
                            </div>
                            <div>
                              <Label>{t('students.section')}</Label>
                              <Select value={reassignForm.section_id || ''} onValueChange={(v) => setReassignForm((f) => ({ ...f, section_id: v }))} disabled={!reassignForm.class_id}>
                                <SelectTrigger data-testid={`reassign-section-${s.id}`}><SelectValue placeholder="-" /></SelectTrigger>
                                <SelectContent>{reassignSections.map((sec) => <SelectItem key={sec.id} value={sec.id}>{sec.name}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                              <Button variant="outline" size="sm" onClick={() => { setReassignFor(null); setReassignSections([]); }} data-testid={`reassign-cancel-${s.id}`}>{t('common.cancel')}</Button>
                              <Button size="sm" onClick={() => saveReassign(s.id)} disabled={reassigning || !reassignForm.class_id} data-testid={`reassign-save-${s.id}`}>{reassigning ? t('common.saving') : t('common.save')}</Button>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Popover
                        open={sectionEditFor === s.id}
                        onOpenChange={(o) => { if (o) openSectionEdit(s); else { setSectionEditFor(null); setSectionEditOptions([]); } }}
                      >
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="gap-2" data-testid={`change-section-button-${s.id}`}>
                            <Layers className="w-4 h-4" /> {t('enroll.changeSection', 'Change section')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72" align="end" data-testid={`change-section-popover-${s.id}`}>
                          <div className="space-y-3">
                            <div>
                              <p className="text-sm font-medium">{t('enroll.changeSection', 'Change section')}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{s.name} &middot; {s.class_name}</p>
                            </div>
                            <div>
                              <Label>{t('students.section')}</Label>
                              <Select value={sectionEditValue || ''} onValueChange={setSectionEditValue}>
                                <SelectTrigger data-testid={`change-section-select-${s.id}`}><SelectValue placeholder="-" /></SelectTrigger>
                                <SelectContent>
                                  {sectionEditOptions.length === 0
                                    ? <div className="px-3 py-2 text-sm text-muted-foreground">{t('enroll.noSections', 'No sections in this class')}</div>
                                    : sectionEditOptions.map((sec) => <SelectItem key={sec.id} value={sec.id}>{sec.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                              <Button variant="outline" size="sm" onClick={() => { setSectionEditFor(null); setSectionEditOptions([]); }} data-testid={`change-section-cancel-${s.id}`}>{t('common.cancel')}</Button>
                              <Button size="sm" onClick={() => saveSectionEdit(s)} disabled={sectionEditing || sectionEditValue === (s.section_id || '')} data-testid={`change-section-save-${s.id}`}>{sectionEditing ? t('common.saving') : t('common.save')}</Button>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                      {(() => {
                        const enrolled = enrollmentsOf(s);
                        const currentEnr = enrolled.find((e) => e.class_id === s.class_id);
                        const canRemove = enrolled.length > 1 && !!currentEnr;
                        const btn = (
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2 text-destructive hover:text-destructive"
                            disabled={!canRemove}
                            onClick={() => canRemove && setRemoveTarget({ student: s, enrollmentId: currentEnr.id, className: s.class_name })}
                            data-testid={`remove-enrollment-${s.id}`}
                          >
                            <Trash2 className="w-4 h-4" /> {t('enroll.removeClass', 'Remove from class')}
                          </Button>
                        );
                        if (canRemove) return btn;
                        return (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild><span tabIndex={0}>{btn}</span></TooltipTrigger>
                              <TooltipContent>{t('enroll.minOneClass', 'A student must be enrolled in at least one class.')}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="enroll-dialog">
          <DialogHeader><DialogTitle>{t('enroll.assign')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('dash.students')} *</Label>
              <Popover open={studentPickerOpen} onOpenChange={setStudentPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={studentPickerOpen}
                    className="w-full justify-between font-normal"
                    data-testid="enroll-student-select"
                  >
                    <span className={selectedStudent ? '' : 'text-muted-foreground'}>
                      {selectedStudent ? `${selectedStudent.name} (${selectedStudent.admission_no})` : t('enroll.searchStudent', 'Search student by name...')}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={t('enroll.searchByName', 'Search by name...')}
                      value={studentQuery}
                      onValueChange={setStudentQuery}
                      data-testid="enroll-student-search"
                    />
                    <CommandList>
                      <CommandEmpty>{t('enroll.noStudentsFound', 'No students found')}</CommandEmpty>
                      <CommandGroup>
                        {filteredStudents.slice(0, 50).map((s) => (
                          <CommandItem
                            key={s.id}
                            value={s.id}
                            onSelect={() => {
                              setForm((f) => ({ ...f, student_id: s.id }));
                              setStudentPickerOpen(false);
                              setStudentQuery('');
                            }}
                            data-testid={`enroll-student-option-${s.id}`}
                          >
                            <Check className={`mr-2 h-4 w-4 ${form.student_id === s.id ? 'opacity-100' : 'opacity-0'}`} />
                            <span className="flex-1 truncate">{s.name}</span>
                            <span className="text-xs text-muted-foreground ml-2 shrink-0">{s.admission_no}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('students.class')} *</Label>
                <Select value={form.class_id || ''} onValueChange={onDialogClass}>
                  <SelectTrigger data-testid="enroll-dialog-class"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('students.section')} *</Label>
                <Select value={form.section_id || ''} onValueChange={(v) => setForm({ ...form, section_id: v })} disabled={!form.class_id}>
                  <SelectTrigger data-testid="enroll-dialog-section"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent>{dialogSections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>{t('students.rollNo')}</Label><Input value={form.roll_no} onChange={(e) => setForm({ ...form, roll_no: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button><Button onClick={save} disabled={saving || !form.class_id || !form.section_id} data-testid="enroll-save-button">{saving ? t('common.saving') : t('common.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}>
        <AlertDialogContent data-testid="remove-enrollment-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('enroll.removeClass', 'Remove from class')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('enroll.removeConfirm', 'Remove {name} from {class}? This will not delete the student — only this class enrollment is removed.')
                .replace('{name}', removeTarget?.student?.name || '')
                .replace('{class}', removeTarget?.className || '')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-enrollment-cancel">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} disabled={removing} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="remove-enrollment-confirm">
              {removing ? t('common.saving') : t('enroll.removeClass', 'Remove from class')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Paired-class section conflict: confirm before overwriting */}
      <AlertDialog open={!!pairConflict} onOpenChange={(o) => { if (!o) setPairConflict(null); }}>
        <AlertDialogContent data-testid="pair-conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('enroll.pairConflictTitle', 'Paired class section conflict')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('enroll.pairConflictBody', 'This student is already enrolled in the paired class under a different section. Confirm to overwrite the paired section so both stay in sync.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1 text-sm" data-testid="pair-conflict-list">
            {(pairConflict?.conflicts || []).map((c, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                <span className="font-medium">{c.class_name}</span>
                <span className="text-muted-foreground">{(c.current_section || '—')} {'\u2192'} {(c.target_section || '—')}</span>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pair-conflict-cancel">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPairOverwrite} disabled={pairConfirming} data-testid="pair-conflict-confirm">
              {pairConfirming ? t('common.saving') : t('enroll.pairConflictConfirm', 'Overwrite & sync')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Super-admin: type-to-confirm bulk class unenroll */}
      <Dialog open={!!bulkTarget} onOpenChange={(o) => { if (!o && !bulkRemoving) { setBulkTarget(null); setBulkConfirmText(''); setBulkPreview(null); } }}>
        <DialogContent data-testid="bulk-remove-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" /> {t('enroll.removeEnrollment', 'Remove Enrollment')}
            </DialogTitle>
            <DialogDescription>
              {t('enroll.bulkDialogDesc', 'This permanently removes all enrollment records for this class in the selected academic year. It cannot be undone.')}
            </DialogDescription>
          </DialogHeader>

          {bulkPreviewLoading ? (
            <div className="flex items-center gap-2 py-6 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading', 'Loading…')}</div>
          ) : bulkPreview ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2 text-sm" data-testid="bulk-remove-summary">
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">{t('students.class', 'Class')}</span><span className="font-medium text-right">{bulkPreview.class_name}</span></div>
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">{t('academicYear.title', 'Academic year')}</span><span className="font-medium text-right">{bulkPreview.academic_year_name || '-'}</span></div>
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">{t('enroll.studentsToUnenroll', 'Students to be unenrolled')}</span><span className="font-semibold text-destructive tabular-nums text-right" data-testid="bulk-remove-count">{bulkPreview.student_count}</span></div>
              </div>

              {bulkPreview.student_count === 0 ? (
                <p className="text-sm text-muted-foreground">{t('enroll.nothingToRemove', 'There are no enrollment records for this class in the selected year.')}</p>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="bulk-confirm-input">
                    {t('enroll.typeToConfirm', 'Type the class name to confirm')}: <span className="font-semibold text-foreground">{bulkPreview.class_name}</span>
                  </Label>
                  <Input
                    id="bulk-confirm-input"
                    value={bulkConfirmText}
                    onChange={(e) => setBulkConfirmText(e.target.value)}
                    placeholder={bulkPreview.class_name}
                    autoComplete="off"
                    data-testid="bulk-confirm-input"
                  />
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => { if (!bulkRemoving) { setBulkTarget(null); setBulkConfirmText(''); setBulkPreview(null); } }} data-testid="bulk-remove-cancel">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={confirmBulkRemove}
              disabled={bulkRemoving || bulkPreviewLoading || !bulkPreview || bulkPreview.student_count === 0 || !bulkNameMatches}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-2"
              data-testid="bulk-remove-confirm"
            >
              {bulkRemoving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {bulkRemoving ? t('common.saving', 'Working…') : t('enroll.confirmUnenroll', 'Unenroll all')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Super-admin: audit trail */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-2xl" data-testid="audit-trail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5" /> {t('enroll.auditTrail', 'Audit trail')}</DialogTitle>
            <DialogDescription>{t('enroll.auditDesc', 'Recent bulk class unenroll actions for this organization.')}</DialogDescription>
          </DialogHeader>
          {auditLoading ? (
            <div className="flex items-center gap-2 py-6 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading', 'Loading…')}</div>
          ) : auditRows.length === 0 ? (
            <EmptyState icon={History} title={t('enroll.noAudit', 'No audit records yet')} description={t('enroll.noAuditDesc', 'Bulk unenroll actions will appear here.')} />
          ) : (
            <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-2 py-2">{t('common.date', 'When')}</th>
                  <th className="px-2 py-2">{t('common.action', 'Action')}</th>
                  <th className="px-2 py-2">{t('common.details', 'Details')}</th>
                  <th className="px-2 py-2">{t('academicYear.title', 'Year')}</th>
                  <th className="px-2 py-2">{t('users.name', 'By')}</th>
                </tr></thead>
                <tbody>
                  {auditRows.map((a) => {
                    const isSync = a.action === 'bulk_sync_paired_classes';
                    const isImport = a.action === 'import_enrollment_excel';
                    const actionLabel = isSync ? t('pairs.syncAction', 'Paired sync')
                      : isImport ? t('imp.action', 'Excel import')
                      : t('enroll.unenrollAction', 'Unenroll');
                    let details;
                    if (isSync) details = `${a.pair_count ?? 0} ${t('pairs.pairsWord', 'pairs')} · +${a.total_added ?? 0} / -${a.total_removed ?? 0}`;
                    else if (isImport) details = `${a.file_name || '—'} · ${a.summary?.students_enrolled ?? 0} ${t('imp.enrolled', 'enrolled')} · ${a.summary?.paired_created ?? 0} ${t('imp.paired', 'paired')}`;
                    else details = `${a.class_name || '-'} · ${a.student_count ?? a.records_removed ?? 0} ${t('pairs.removed', 'removed')}`;
                    return (
                      <tr key={a.id} className="border-b border-border last:border-0" data-testid={`audit-row-${a.id}`}>
                        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{a.timestamp ? new Date(a.timestamp).toLocaleString() : '-'}</td>
                        <td className="px-2 py-2">
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wide">{actionLabel}</Badge>
                        </td>
                        <td className="px-2 py-2">{details}</td>
                        <td className="px-2 py-2">{a.academic_year_name || '-'}</td>
                        <td className="px-2 py-2 text-muted-foreground">{a.actor_name || a.actor_email || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditOpen(false)} data-testid="audit-trail-close">{t('common.close', 'Close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
